import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";

import BooleanIcon from "veneer/BooleanIcon";
import Button from "veneer/Button";
import DataTable, {
  createColumnHelper,
  createFilter,
  sorting,
} from "veneer/DataTable";
import DestructiveConfirmationDialog from "veneer/DestructiveConfirmationDialog";
import { DeleteIconButton } from "veneer/IconButton";
import List from "veneer/List";
import ListItem from "veneer/ListItem";
import { OverflowTooltipTypography } from "veneer/Typography";

import { DELETE_ALERT_V3 } from "cortex/ui/src/fleet/redux-store/alerts-v3/actions";
import {
  alertsSelector,
  alertsStoresLoadedSelector,
} from "cortex/ui/src/fleet/redux-store/alerts-v3/selectors";
import { Application } from "cortex/ui/src/fleet/view/application/model";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas/actions";
import {
  applicationsStoresLoadedSelector,
  compatApplicationsSelector,
} from "cortex/ui/src/redux-store/applications/selectors";
import { getAuthUser } from "cortex/ui/src/redux-store/auth/selectors";
import {
  deviceStoresLoadedSelector,
  devicesSelector,
} from "cortex/ui/src/redux-store/devices/selectors";
import { makeGetAllAsArray } from "cortex/ui/src/redux-store/selectors";
import {
  isAdmin,
  isReadonly,
  usersSelector,
  usersStoresLoadedSelector,
} from "cortex/ui/src/redux-store/users/selectors";
import { LoadingIndicator } from "cortex/ui/src/ui/loader";
import { User } from "cortex/ui/src/user/model";
import { getTargetOptions } from "cortex/ui/src/util/getTargetOptions";
import { Container } from "cortex/ui/src/view/layout";

import AlertCreateDrawer from "./AlertCreateDrawer";
import AlertEditDrawer from "./AlertEditDrawer";
import { Alert, DrawerType, FormAction, TargetDevice } from "../model";

const getAlertsAsArray = makeGetAllAsArray();
const getAppsAsArray = makeGetAllAsArray();
const getUsersAsArray = makeGetAllAsArray();
const basePath = "/diagnostics/alerts";
const filter = createFilter<Alert>("name");

const column = createColumnHelper<Alert>();

export default function AlertList() {
  const dispatch = useAppDispatch();
  const { action, id } = useParams<{ action: FormAction; id: string }>();
  const history = useHistory();
  const applicationsById = useAppSelector(compatApplicationsSelector);
  const applications = getAppsAsArray({
    byId: applicationsById,
  }) as unknown as ReadonlyArray<Application>;
  const applicationsStoresLoaded = useAppSelector(
    applicationsStoresLoadedSelector,
  );
  const alertsById = useAppSelector(alertsSelector);
  const alerts = getAlertsAsArray({
    byId: alertsById,
  }) as unknown as ReadonlyArray<Alert>;
  const alertsStoresLoaded = useAppSelector(alertsStoresLoadedSelector);
  const devices = useAppSelector(devicesSelector);
  const devicesStoresLoaded = useAppSelector(deviceStoresLoadedSelector);
  const usersById = useAppSelector(usersSelector);
  const users = getUsersAsArray({
    byId: usersById,
  }) as unknown as ReadonlyArray<User>;
  const user = useAppSelector(getAuthUser);
  const userIsAdmin = isAdmin(user);
  const userIsReadonly = isReadonly(user);
  const usersStoresLoaded = useAppSelector(usersStoresLoadedSelector);
  const targetOpts = getTargetOptions(devices);
  const storesLoaded = useMemo(
    () =>
      alertsStoresLoaded &&
      applicationsStoresLoaded &&
      devicesStoresLoaded &&
      usersStoresLoaded,
    [
      alertsStoresLoaded,
      applicationsStoresLoaded,
      devicesStoresLoaded,
      usersStoresLoaded,
    ],
  );
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [alertToDelete, setAlertToDelete] = useState<Alert | null>(null);

  const [alertDrawerType, setAlertDrawerType] = useState<DrawerType>(
    DrawerType.NONE,
  );

  useEffect(() => {
    if (userIsReadonly && action) {
      history.replace("/404");
      return;
    } else if (action == FormAction.CREATE) {
      setAlertDrawerType(DrawerType.CREATE);
      return;
    } else if (action == FormAction.EDIT && id && alertsById[id]) {
      setAlertDrawerType(DrawerType.EDIT);
      return;
    }
    setAlertDrawerType(DrawerType.NONE);
  }, [action, alertsById, history]);

  const handleCloseDrawer = () => {
    history.push(basePath);
  };

  const columns = useMemo(
    () => [
      column.accessor("name", {
        header: "Name",
        cell: (prop) => {
          const value = prop.getValue();
          if (!userIsAdmin) {
            return value;
          }
          return (
            <Link to={`${basePath}/${prop.row.original.id}/edit`}>{value}</Link>
          );
        },
        meta: {
          primary: true,
        },
        sortingFn: "alphanumeric",
      }),
      column.accessor("notification_only", {
        header: "Triggered Alerts",
        cell: (prop) => {
          return <BooleanIcon value={!prop.getValue()} />;
        },
        sortingFn: sorting.boolean((row) => !row.notification_only),
      }),
      column.accessor("target_devices", {
        header: "Target",
        cell: (prop) => {
          const targets = Object.values(
            (prop.getValue() ?? {}) as Record<string, TargetDevice>,
          );
          return (
            <List dense disablePadding>
              {targets.map((target) => {
                const linkType = target.type === "d" ? "device" : "tag";
                const unique = `${target.type}-${target.id}`;
                return (
                  <ListItem disablePadding key={unique}>
                    {target.is_deleted ? (
                      <OverflowTooltipTypography variant="body2">{`${linkType}: ${target.name}`}</OverflowTooltipTypography>
                    ) : (
                      <Link to={`/${linkType}s/${target.id}`}>
                        {`${linkType}: ${target.name}`}
                      </Link>
                    )}
                  </ListItem>
                );
              })}
            </List>
          );
        },
        meta: {
          primary: true,
        },
        enableSorting: false,
      }),
      column.accessor("user_ids", {
        header: "Users",
        cell: (prop) => {
          const value = prop.getValue();
          const alertUsers = users.filter((u) => value.includes(u.id));
          const allUsers = alertUsers.map(
            (user) => `${user.name} <${user.email}>`,
          );

          return (
            <List dense disablePadding>
              {allUsers.map((user) => (
                <ListItem disablePadding key={user}>
                  <OverflowTooltipTypography variant="body2">
                    {user}
                  </OverflowTooltipTypography>
                </ListItem>
              ))}
            </List>
          );
        },
        meta: {
          primary: true,
        },
        enableSorting: false,
      }),
      column.display({
        id: "operations",
        cell: (prop) => {
          const row = prop.row.original;
          return (
            <DeleteIconButton
              edge="end"
              onClick={() => {
                setAlertToDelete(row);
                setShowDeleteModal(true);
              }}
            />
          );
        },
      }),
    ],
    [users, usersById],
  );

  const handleDeleteAlert = async () => {
    if (alertToDelete) {
      setShowDeleteModal(false);
      try {
        await createPromiseApiAction(dispatch, DELETE_ALERT_V3, [
          [alertToDelete.id],
        ]);
      } catch (e) {
        console.error("Failed to delete alert:", e);
      } finally {
        setAlertToDelete(null);
      }
    }
  };

  const addButton = !userIsReadonly ? (
    <Link to={`${basePath}/create`}>
      <Button>Add Alert</Button>
    </Link>
  ) : null;

  return (
    <Container title="Alerts">
      {storesLoaded ? (
        <DataTable
          columns={columns}
          filter={filter}
          hideColumns={!userIsAdmin ? ["operations"] : []}
          initialPageSize={10}
          primaryActions={addButton}
          rows={alerts}
        />
      ) : (
        <LoadingIndicator />
      )}
      <DestructiveConfirmationDialog
        commitLabel="Delete"
        onCancel={() => {
          setAlertToDelete(null);
          setShowDeleteModal(false);
        }}
        onCommit={handleDeleteAlert}
        open={!!showDeleteModal}
        title="Are you sure?"
        variant="modal"
      >
        This alert will be deleted permanently.
      </DestructiveConfirmationDialog>
      {alertDrawerType === DrawerType.CREATE && (
        <AlertCreateDrawer
          applications={applications}
          onCancel={handleCloseDrawer}
          open={true}
          targetOpts={targetOpts}
          users={users}
        />
      )}
      {alertDrawerType === DrawerType.EDIT && id && alertsById[Number(id)] && (
        <AlertEditDrawer
          alert={alertsById[Number(id)]}
          applications={applications}
          onCancel={handleCloseDrawer}
          open={true}
          targetOpts={targetOpts}
          users={users}
        />
      )}
    </Container>
  );
}
